import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import {
  BaseActivity,
  BaseActivityArticleName,
  BaseActivityDetailedContent,
  BaseActivityText,
} from '@/components/activities/BaseActivity/BaseActivity';
import Stack from '@/design_system/Stack';
import IconRefusal from '@/icons/Refusal.svg';
import IconStore from '@/icons/Store.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import IconValidation from '@/icons/Validation.svg';
import { useDatabaseTranslation } from '@/services/i18n';

export const ArticleQualityControlCompletedActivity = ({
  activity,
  displayArticleName,
}: {
  activity: ActivityOfType<'article_quality_control_completed'>;
  displayArticleName: boolean;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  let creatorName = activity.creator.name;

  if (creatorName && activity.data.answerer) {
    creatorName += ` (${activity.data.answerer})`;
  }

  const articleName = (
    <BaseActivityArticleName activity={activity} displayArticleName={displayArticleName} />
  );

  if (activity.data.koAnswers.length > 0) {
    const reason = activity.data.status ?? 'ko-new-service-choice';
    const comment = activity.data.koNote;
    const koType = activity.data.koType;
    const conditionsKo = activity.data.koAnswers;
    const icon = activity.data.trait === 'store' ? <IconStore /> : <IconStoreRepair />;

    const formatedConditionsKo = conditionsKo.map((condition) => {
      const context = condition.context;

      const contextLine = (
        <>
          {' • '}"{context}"
        </>
      );

      if (condition.question) {
        const questionTitle = _db(condition.question.title);
        const questionAnswer = _db(condition.question.answer.text);

        return (
          <>
            {' • '}
            {questionTitle} {questionAnswer}
            {contextLine}
          </>
        );
      }
      if (condition.action) {
        const actionName = _db(condition.action.name);

        return (
          <>
            {' • '}
            {t({
              id: 'requests.articles.quality-control.actions-questions.title',
              message: `Has the action "${actionName}" been done properly?`,
            })}{' '}
            {t({
              id: 'activities.article_quality_control_completed_ko.action_question',
              message: `No`,
            })}
            {contextLine}
          </>
        );
      }
    });

    return (
      <>
        <BaseActivity
          createdAt={activity.createdAt}
          type="neutral"
          icon={icon}
          message={
            <BaseActivityText>
              {koType ? (
                <>
                  <Trans id="activities.article_quality_control_completed_ko.issue">
                    <strong>{creatorName}</strong> chose the quality control issue option:{' '}
                  </Trans>
                  <strong>
                    {reason === 'ko-keep-workshop' && (
                      <Trans id="activities.article_quality_control_completed_ko.ko-keep-workshop">
                        Rework in workshop
                      </Trans>
                    )}
                    {reason === 'ko-new-service-choice' && (
                      <Trans id="activities.article_quality_control_completed_ko.ko-new-service-choice">
                        Escalate to brand
                      </Trans>
                    )}
                  </strong>
                </>
              ) : (
                <Trans id="activities.article_quality_control_completed_ko.added-note">
                  <strong>{creatorName}</strong> added the following quality control note:
                </Trans>
              )}
            </BaseActivityText>
          }
        >
          <BaseActivityDetailedContent>
            <>{comment}</>
          </BaseActivityDetailedContent>
        </BaseActivity>
        <BaseActivity
          createdAt={activity.createdAt}
          type="danger"
          icon={<IconRefusal />}
          message={
            <BaseActivityText>
              <Trans id="activities.article_quality_control_completed_ko.message">
                <strong>{creatorName}</strong> completed the quality control - Failed
              </Trans>
            </BaseActivityText>
          }
        >
          <Stack gap="0.5rem">
            {formatedConditionsKo.map((line) => (
              <BaseActivityText key={crypto.randomUUID()}>{line}</BaseActivityText>
            ))}
          </Stack>
        </BaseActivity>
      </>
    );
  }

  return (
    <BaseActivity
      createdAt={activity.createdAt}
      type="primary"
      icon={<IconValidation />}
      message={
        <BaseActivityText>
          {displayArticleName ? (
            <Trans id="activities.article_quality_control_completed.message.with-article">
              <strong>{creatorName}</strong> completed the quality control for the item{' '}
              {articleName} - Passed
            </Trans>
          ) : (
            <Trans id="activities.article_quality_control_completed.message">
              <strong>{creatorName}</strong> completed the quality control - Passed
            </Trans>
          )}
        </BaseActivityText>
      }
    />
  );
};
